import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RealtimeClient, socketURL, type Status } from "./socket";

const Ctx = createContext<RealtimeClient | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [client] = useState(() => new RealtimeClient(socketURL()));
  const qc = useQueryClient();
  useEffect(() => {
    client.start();
    const offs = [
      // Config changes elsewhere (another admin) refresh what this tab shows.
      client.on("config.changed", () => qc.invalidateQueries({ queryKey: ["config"] })),
      client.on("reconnected", () => qc.invalidateQueries()),
    ];
    return () => {
      offs.forEach((f) => f());
      client.stop();
    };
  }, [client, qc]);
  return <Ctx.Provider value={client}>{children}</Ctx.Provider>;
}

export function useRealtime(): RealtimeClient {
  const c = useContext(Ctx);
  if (!c) throw new Error("useRealtime outside RealtimeProvider");
  return c;
}

export function useRealtimeEvent(type: string, handler: (data: unknown) => void) {
  const client = useRealtime();
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => client.on(type, (d) => ref.current(d)), [client, type]);
}

export function useRealtimeStatus(): Status {
  const client = useRealtime();
  // useSyncExternalStore re-reads on subscribe: a status change between render and a plain
  // useEffect subscription (the socket is often "ready" by then) would otherwise be lost.
  const subscribe = useCallback((onChange: () => void) => client.onStatus(onChange), [client]);
  return useSyncExternalStore(subscribe, () => client.status);
}

export function useRoom(conversationId: string | undefined) {
  const client = useRealtime();
  useEffect(() => (conversationId ? client.join(conversationId) : undefined), [client, conversationId]);
}
